// ══════════════════════════════════════════════════════════════════
//  ED CRM - Indicador de status de conexão (PR-6 item 17)
// ══════════════════════════════════════════════════════════════════
//  Ponto colorido na navbar:
//    🟢 verde      — online + realtime conectado
//    🟡 amarelo    — reconectando OU navegador online sem realtime
//    🔴 vermelho   — offline (browser sem internet)
//
//  Monitora dois sinais:
//   1) window.online/offline events
//   2) supabase.realtime.connection.connectionState (CONNECTING|OPEN|CLOSED)
//      via polling a cada 2s — sonner.connect/disconnect events não
//      são públicos no JS-SDK 2.x.
//
//  Largura ≤ 24px (apenas o ponto + tooltip via title).
// ══════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Circle } from 'lucide-react';
import { getRealtimeConnectionState } from '@app';

type SyncState = 'online' | 'reconnecting' | 'offline';

const COLORS: Record<SyncState, string> = {
  online:       'var(--success-solid)', // verde
  reconnecting: 'var(--warn-solid)', // amarelo
  offline:      'var(--danger-solid)', // vermelho
};

const TOOLTIPS: Record<SyncState, string> = {
  online:       'Conectado',
  reconnecting: 'Reconectando…',
  offline:      'Sem conexão — dados podem estar desatualizados',
};

// Superfície de runtime do RealtimeClient que consultamos por polling.
const computeStatus = (browserOnline: boolean, realtimeState: string): SyncState => {
  if (!browserOnline) return 'offline';
  if (realtimeState === 'OPEN' || realtimeState === 'open') return 'online';
  return 'reconnecting';
};

const SyncStatus = () => {
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [realtimeState, setRealtimeState] = useState('CONNECTING');

  // Browser online/offline
  useEffect(() => {
    const handleOnline  = () => setBrowserOnline(true);
    const handleOffline = () => setBrowserOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Realtime connection state (polling — SDK não expõe evento público)
  useEffect(() => {
    const tick = () => setRealtimeState(getRealtimeConnectionState());
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, []);

  const status = computeStatus(browserOnline, realtimeState);
  const color  = COLORS[status];

  return (
    <span
      className="nav-sync"
      role="status"
      aria-live="polite"
      title={TOOLTIPS[status]}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        flexShrink: 0,
      }}
    >
      <Circle
        aria-hidden="true"
        size={9}
        fill="currentColor"
        strokeWidth={0}
        style={{
          color,
          transition: 'color 0.2s',
          animation: status === 'reconnecting' ? 'sync-pulse 1.2s infinite' : undefined,
        }}
      />
    </span>
  );
};

// Inject keyframes (pulse para reconectando) só uma vez.
if (typeof document !== 'undefined' && !document.getElementById('sync-pulse-keyframes')) {
  const style = document.createElement('style');
  style.id = 'sync-pulse-keyframes';
  style.textContent = `
    @keyframes sync-pulse {
      0%, 100% { transform: scale(1);   opacity: 1; }
      50%      { transform: scale(0.7); opacity: 0.6; }
    }
  `;
  document.head.appendChild(style);
}

export default SyncStatus;
